import { useEffect, useMemo, useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Skeleton,
    Stack,
    TextField,
    Typography,
    Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useQuery } from '@tanstack/react-query';
import { customFetcher } from '../hooks/customFetcher';
import PageHeader from '../core/PageHeader';
import { useAlert } from '../core/AlertContext';
import { ErrorTryRefetch } from '../core/ErrorTryRefetch';
import {
    InfoBankAccount,
    InfoRoleKey,
    InfoUser,
    infoBankAccountUpsertSchema,
    infoUserSchema,
} from './infoSchemas';
import {
    useAddAdvisor,
    useCreateBankAccount,
    useDeleteAdvisor,
    useDeleteBankAccount,
    useInfo,
    useUpdateAddress,
    useUpdateBankAccount,
    useUpdateRoles,
} from '../hooks/info';
import { useAuthenticatedImage } from '../hooks/useAuthenticatedImage';
import { useCanEditInfo } from './useCanEditInfo';
import { fieldSx, primaryButtonSx, secondaryButtonSx } from '../core/pvtaStyles';

const ROLE_LABELS: Record<InfoRoleKey, string> = {
    ah_chair: 'AH-Vorsitzender',
    ah_vice: 'stellv. AH-Vorsitzender',
    treasurer: 'Kassenwart',
    liaison: 'Verbindungsmann',
    hut_manager: 'Hüttenwart',
    X: 'X',
    XX: 'XX',
    XXX: 'XXX',
};

const ROLE_DISPLAY_ORDER: InfoRoleKey[] = [
    'ah_chair',
    'ah_vice',
    'treasurer',
    'liaison',
    'hut_manager',
    'X',
    'XX',
    'XXX',
];

const parseErrorText = (error: unknown, fallback: string): string => {
    if (!(error instanceof Error)) {
        return fallback;
    }
    return error.message || fallback;
};

const getDisplayName = (user: InfoUser): string => user.name;

const InfoUserAvatar: React.FC<{ user: InfoUser; size?: number }> = ({ user, size = 28 }) => {
    const avatarUrl = user.avatar_url?.trim() ? user.avatar_url : null;
    const { src } = useAuthenticatedImage(avatarUrl);

    return (
        <Avatar
            src={src ?? (user.profile_picture ? `data:image/png;base64,${user.profile_picture}` : undefined)}
            sx={{ width: size, height: size }}
        >
            {getDisplayName(user).slice(0, 1).toUpperCase()}
        </Avatar>
    );
};

const InfoPage: React.FC = () => {
    const canEdit = useCanEditInfo();
    const { showAlert } = useAlert();
    const { info, isLoadingInfo, isErrorInfo, infoError, refetchInfo } = useInfo();
    const updateRolesMutation = useUpdateRoles();
    const addAdvisorMutation = useAddAdvisor();
    const deleteAdvisorMutation = useDeleteAdvisor();
    const createBankAccountMutation = useCreateBankAccount();
    const updateBankAccountMutation = useUpdateBankAccount();
    const deleteBankAccountMutation = useDeleteBankAccount();
    const updateAddressMutation = useUpdateAddress();

    const [newAdvisor, setNewAdvisor] = useState<InfoUser | null>(null);
    const [addressDraft, setAddressDraft] = useState<string>('');
    const [bankDialogOpen, setBankDialogOpen] = useState<boolean>(false);
    const [editingBankAccount, setEditingBankAccount] = useState<InfoBankAccount | null>(null);
    const [hasShownLoadError, setHasShownLoadError] = useState<boolean>(false);
    const [bankForm, setBankForm] = useState({
        display_name: '',
        account_holder: '',
        iban: '',
        bic: '',
        notes: '',
    });

    const { data: usersLight = [] } = useQuery<InfoUser[]>({
        queryKey: ['users-light-info'],
        queryFn: async () => {
            const response = await customFetcher<unknown[]>({ endpoint: '/users/?minimal=1' });
            return response
                .map((item: unknown) => {
                    if (typeof item !== 'object' || item === null) {
                        return null;
                    }
                    const source = item as Record<string, unknown>;
                    const firstName = typeof source.first_name === 'string' ? source.first_name : '';
                    const lastName = typeof source.last_name === 'string' ? source.last_name : '';
                    const fullName = `${firstName} ${lastName}`.trim();
                    const fallbackName = typeof source.email === 'string' ? source.email : 'Unbekannt';
                    return {
                        id: source.id,
                        name: fullName || fallbackName,
                        avatar_url: typeof source.avatar_url === 'string' ? source.avatar_url : null,
                        profile_picture: typeof source.profile_picture === 'string' ? source.profile_picture : null,
                    };
                })
                .flatMap((candidate) => {
                    const parsed = infoUserSchema.safeParse(candidate);
                    return parsed.success ? [parsed.data] : [];
                });
        },
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (info) {
            setAddressDraft(info.address);
        }
    }, [info]);

    useEffect(() => {
        if (!isErrorInfo || !infoError || hasShownLoadError) {
            return;
        }
        showAlert({
            severity: 'error',
            text: `Infos konnten nicht geladen werden: ${parseErrorText(infoError, 'Unbekannter Fehler')}`,
        });
        setHasShownLoadError(true);
    }, [hasShownLoadError, infoError, isErrorInfo, showAlert]);

    const roleKeys = useMemo(() => {
        if (!info) {
            return ROLE_DISPLAY_ORDER;
        }
        const backendRoleKeys = (Object.keys(info.roles) as Array<InfoRoleKey | 'advisors'>)
            .filter((key): key is InfoRoleKey => key !== 'advisors');
        const orderedKnownKeys = ROLE_DISPLAY_ORDER.filter((key) => backendRoleKeys.includes(key));
        const unknownKeys = backendRoleKeys.filter((key) => !ROLE_DISPLAY_ORDER.includes(key));
        return [...orderedKnownKeys, ...unknownKeys];
    }, [info]);

    const usersById = useMemo(() => {
        const mapping = new Map<number, InfoUser>();
        usersLight.forEach((user) => {
            mapping.set(user.id, user);
        });
        return mapping;
    }, [usersLight]);

    const isInfoEmpty = useMemo(() => {
        if (!info) {
            return false;
        }
        const hasRoleAssignment = roleKeys.some((roleKey) => info.roles[roleKey] !== null);
        const hasAdvisors = info.roles.advisors.length > 0;
        const hasBankAccounts = info.bank_accounts.length > 0;
        const hasAddress = info.address.trim().length > 0;
        return !hasRoleAssignment && !hasAdvisors && !hasBankAccounts && !hasAddress;
    }, [info, roleKeys]);

    const handleRoleChange = async (roleKey: InfoRoleKey, nextUser: InfoUser | null): Promise<void> => {
        if (!canEdit) {
            return;
        }

        try {
            await updateRolesMutation.mutateAsync({
                roles: {
                    [roleKey]: nextUser?.id ?? null,
                },
            });
            showAlert({ severity: 'success', text: 'Rolle aktualisiert.' });
        } catch (error) {
            showAlert({ severity: 'error', text: parseErrorText(error, 'Rolle konnte nicht aktualisiert werden.') });
        }
    };

    const handleAddAdvisor = async (): Promise<void> => {
        if (!newAdvisor || !canEdit) {
            return;
        }

        try {
            await addAdvisorMutation.mutateAsync({ user_id: newAdvisor.id });
            setNewAdvisor(null);
            showAlert({ severity: 'success', text: 'Beisitzer hinzugefügt.' });
        } catch (error) {
            showAlert({ severity: 'error', text: parseErrorText(error, 'Beisitzer konnte nicht hinzugefügt werden.') });
        }
    };

    const handleDeleteAdvisor = async (advisorId: number): Promise<void> => {
        if (!canEdit) {
            return;
        }

        try {
            await deleteAdvisorMutation.mutateAsync({ advisor_id: advisorId });
            showAlert({ severity: 'success', text: 'Beisitzer entfernt.' });
        } catch (error) {
            showAlert({ severity: 'error', text: parseErrorText(error, 'Beisitzer konnte nicht entfernt werden.') });
        }
    };

    const handleOpenCreateBank = (): void => {
        setEditingBankAccount(null);
        setBankForm({
            display_name: '',
            account_holder: '',
            iban: '',
            bic: '',
            notes: '',
        });
        setBankDialogOpen(true);
    };

    const handleOpenEditBank = (account: InfoBankAccount): void => {
        setEditingBankAccount(account);
        setBankForm({
            display_name: account.display_name,
            account_holder: account.account_holder,
            iban: account.iban ?? '',
            bic: account.bic ?? '',
            notes: account.notes ?? '',
        });
        setBankDialogOpen(true);
    };

    const handleSaveBank = async (): Promise<void> => {
        const parsed = infoBankAccountUpsertSchema.safeParse(bankForm);
        if (!parsed.success) {
            showAlert({ severity: 'error', text: parsed.error.issues[0]?.message ?? 'Bankdaten sind ungültig.' });
            return;
        }

        try {
            if (editingBankAccount) {
                await updateBankAccountMutation.mutateAsync({
                    id: editingBankAccount.id,
                    ...parsed.data,
                });
                showAlert({ severity: 'success', text: 'Bankverbindung aktualisiert.' });
            } else {
                await createBankAccountMutation.mutateAsync(parsed.data);
                showAlert({ severity: 'success', text: 'Bankverbindung erstellt.' });
            }
            setBankDialogOpen(false);
            setEditingBankAccount(null);
        } catch (error) {
            showAlert({ severity: 'error', text: parseErrorText(error, 'Bankverbindung konnte nicht gespeichert werden.') });
        }
    };

    const handleDeleteBankAccount = async (id: number): Promise<void> => {
        if (!canEdit) {
            return;
        }
        try {
            await deleteBankAccountMutation.mutateAsync({ id });
            showAlert({ severity: 'success', text: 'Bankverbindung gelöscht.' });
        } catch (error) {
            showAlert({ severity: 'error', text: parseErrorText(error, 'Bankverbindung konnte nicht gelöscht werden.') });
        }
    };

    const handleSaveAddress = async (): Promise<void> => {
        if (!canEdit) {
            return;
        }

        try {
            await updateAddressMutation.mutateAsync({ address: addressDraft });
            showAlert({ severity: 'success', text: 'Anschrift gespeichert.' });
        } catch (error) {
            showAlert({ severity: 'error', text: parseErrorText(error, 'Anschrift konnte nicht gespeichert werden.') });
        }
    };

    if (isLoadingInfo) {
        return (
            <Box sx={{ py: { xs: 3, md: 4 } }}>
                <Container maxWidth="xl">
                    <Stack spacing={2}>
                        <Skeleton variant="text" width={140} height={44} />
                        <Grid container spacing={2}>
                            {[0, 1].map((item) => (
                                <Grid item xs={12} md={6} key={item}>
                                    <Card variant="outlined" sx={{ height: '100%' }}>
                                        <CardHeader title={<Skeleton variant="text" width="45%" />} />
                                        <CardContent>
                                            <Stack spacing={1.5}>
                                                {[0, 1, 2, 3].map((row) => (
                                                    <Stack
                                                        key={row}
                                                        direction={{ xs: 'column', md: 'row' }}
                                                        spacing={1}
                                                        alignItems={{ xs: 'flex-start', md: 'center' }}
                                                    >
                                                        <Skeleton variant="text" width={180} />
                                                        <Skeleton variant="rounded" height={40} sx={{ flex: 1, width: '100%' }} />
                                                    </Stack>
                                                ))}
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Stack>
                </Container>
            </Box>
        );
    }

    if (isErrorInfo || !info) {
        return (
            <ErrorTryRefetch refetchFunction={() => {
                void refetchInfo();
            }} />
        );
    }

    return (
        <Box sx={{ py: { xs: 3, md: 4 } }}>
            <Container maxWidth="xl">
                <PageHeader title="Infos" subtitle="Vorstand, Ämter, Bankverbindungen und Anschrift der Verbindung." />

                {isInfoEmpty && (
                    <Box
                        sx={{
                            mb: 2,
                            px: 1.5,
                            py: 1,
                            borderRadius: 1.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                        }}
                    >
                        Noch keine Infos hinterlegt. Du kannst jetzt Rollen, Bankverbindungen und Anschrift einpflegen.
                    </Box>
                )}
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardHeader title="Vorstand & Ämter" />
                            <CardContent>
                                <Stack spacing={1.5}>
                                    {roleKeys.map((roleKey) => {
                                        const selected = info.roles[roleKey] ?? null;
                                        return (
                                            <Stack key={roleKey} direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', md: 'center' }}>
                                                <Typography sx={{ minWidth: { md: 220 }, fontWeight: 600 }}>
                                                    {ROLE_LABELS[roleKey] ?? roleKey}
                                                </Typography>
                                                <Autocomplete
                                                    fullWidth
                                                    options={usersLight}
                                                    getOptionLabel={(option) => getDisplayName(option)}
                                                    value={selected ? usersById.get(selected.id) ?? selected : null}
                                                    onChange={(_event, value) => {
                                                        void handleRoleChange(roleKey, value);
                                                    }}
                                                    disabled={!canEdit || updateRolesMutation.isPending}
                                                    renderOption={(props, option) => (
                                                        <Box component="li" {...props}>
                                                            <Stack direction="row" spacing={1} alignItems="center">
                                                                <InfoUserAvatar user={option} />
                                                                <Typography variant="body2">{option.name}</Typography>
                                                            </Stack>
                                                        </Box>
                                                    )}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            size="small"
                                                            placeholder="Person auswählen"
                                                            sx={fieldSx}
                                                        />
                                                    )}
                                                />
                                            </Stack>
                                        );
                                    })}

                                    <Typography variant="subtitle2" sx={{ mt: 1 }}>
                                        Beisitzer
                                    </Typography>
                                    <Stack spacing={1}>
                                        {info.roles.advisors.map((advisor) => (
                                            <Stack key={advisor.id} direction="row" spacing={1} alignItems="center">
                                                <InfoUserAvatar user={advisor} />
                                                <Typography sx={{ flex: 1 }}>{advisor.name}</Typography>
                                                {canEdit && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            void handleDeleteAdvisor(advisor.advisor_id ?? advisor.id);
                                                        }}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Stack>
                                        ))}
                                    </Stack>

                                    {canEdit && (
                                        <Box sx={{ maxWidth: 860 }}>
                                            <Grid container spacing={1.25}>
                                                <Grid item xs={12} sm={6}>
                                                    <Autocomplete
                                                        fullWidth
                                                        options={usersLight}
                                                        value={newAdvisor}
                                                        getOptionLabel={(option) => getDisplayName(option)}
                                                        onChange={(_event, value) => setNewAdvisor(value)}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                size="small"
                                                                placeholder="Beisitzer auswählen"
                                                                sx={fieldSx}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Button
                                                        fullWidth
                                                        variant="outlined"
                                                        startIcon={<AddIcon />}
                                                        onClick={() => {
                                                            void handleAddAdvisor();
                                                        }}
                                                        disabled={!newAdvisor || addAdvisorMutation.isPending}
                                                        sx={{ height: '100%' }}
                                                    >
                                                        Beisitzer hinzufügen
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardHeader title="Sonstiges" />
                            <CardContent>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={8}
                                    placeholder="Da kann man alles reinschreiben, was man will."
                                    value={addressDraft}
                                    onChange={({ target }) => setAddressDraft(target.value)}
                                    InputProps={{
                                        readOnly: !canEdit,
                                    }}
                                    sx={{
                                        ...fieldSx,
                                        backgroundColor: 'background.paper',
                                        '& .MuiOutlinedInput-root': {
                                            alignItems: 'flex-start',
                                            backgroundColor: 'background.paper',
                                        },
                                        '& .MuiInputBase-inputMultiline': {
                                            maxHeight: 320,
                                            overflowY: 'auto',
                                        },
                                    }}
                                />
                                {canEdit && (
                                    <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                                        <Button
                                            sx={{ ...primaryButtonSx }}
                                            size="small"
                                            onClick={() => {
                                                void handleSaveAddress();
                                            }}
                                            disabled={updateAddressMutation.isPending}
                                        >
                                            Speichern
                                        </Button>
                                    </Stack>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Card variant="outlined">
                            <CardHeader
                                title="Bankverbindungen"
                                action={canEdit ? (
                                    <Button size="small" variant="outlined" onClick={handleOpenCreateBank}>
                                        Neu
                                    </Button>
                                ) : undefined}
                            />
                            <CardContent>
                                <Grid container spacing={1.25}>
                                    {info.bank_accounts.length === 0 && (
                                        <Grid item xs={12}>
                                            <Typography variant="body2" color="text.secondary">
                                                Keine Bankverbindungen hinterlegt.
                                            </Typography>
                                        </Grid>
                                    )}
                                    {info.bank_accounts.map((account) => (
                                        <Grid item xs={12} sm={6} md={3} key={account.id}>
                                            <Card variant="outlined">
                                                <CardContent sx={{ '&:last-child': { pb: 1.5 } }}>
                                                    <Stack spacing={0.5}>
                                                        <Typography sx={{ fontWeight: 700 }}>{account.display_name}</Typography>
                                                        <Typography variant="body2">{account.account_holder}</Typography>
                                                        {account.iban && <Typography variant="caption" color="text.secondary">{`IBAN: ${account.iban}`}</Typography>}
                                                        {account.bic && <Typography variant="caption" color="text.secondary">{`BIC: ${account.bic}`}</Typography>}
                                                        {account.notes && <Typography variant="caption" color="text.secondary">{account.notes}</Typography>}
                                                    </Stack>
                                                    {canEdit && (
                                                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ mt: 1 }}>
                                                            <IconButton size="small" onClick={() => handleOpenEditBank(account)}>
                                                                <EditOutlinedIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => {
                                                                    void handleDeleteBankAccount(account.id);
                                                                }}
                                                            >
                                                                <DeleteOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        </Stack>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Container>

            <Dialog open={bankDialogOpen} onClose={() => setBankDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editingBankAccount ? 'Bankverbindung bearbeiten' : 'Bankverbindung hinzufügen'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={1} sx={{ mt: 1 }}>
                        <TextField
                            label="Bezeichnung"
                            value={bankForm.display_name}
                            onChange={({ target }) => setBankForm((previous) => ({ ...previous, display_name: target.value }))}
                            sx={fieldSx}
                        />
                        <TextField
                            label="Kontoinhaber"
                            value={bankForm.account_holder}
                            onChange={({ target }) => setBankForm((previous) => ({ ...previous, account_holder: target.value }))}
                            sx={fieldSx}
                        />
                        <TextField
                            label="IBAN"
                            value={bankForm.iban}
                            onChange={({ target }) => setBankForm((previous) => ({ ...previous, iban: target.value }))}
                            sx={fieldSx}
                        />
                        <TextField
                            label="BIC"
                            value={bankForm.bic}
                            onChange={({ target }) => setBankForm((previous) => ({ ...previous, bic: target.value }))}
                            sx={fieldSx}
                        />
                        <TextField
                            label="Notizen"
                            value={bankForm.notes}
                            multiline
                            minRows={2}
                            onChange={({ target }) => setBankForm((previous) => ({ ...previous, notes: target.value }))}
                            sx={fieldSx}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button sx={secondaryButtonSx} onClick={() => setBankDialogOpen(false)}>Abbrechen</Button>
                    <Button sx={{ ...primaryButtonSx }} onClick={() => void handleSaveBank()}>
                        Speichern
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default InfoPage;
